import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TrendIndicator } from "../../../../common/components/TrendIndicator/TrendIndicator";
import { IconArrowLink } from "../../../../common/icons/IconArrowLink/IconArrowLink";
import defaultContent from "../../../../content/groupManagement";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import "./ActionCard.scss";
import type { MoMTrend } from "../../../../services/GroupManagementOverview/groupManagementOverviewAPI.types";

export type ActionCardRow = {
  kpiValue: string | number;
  kpiName: string;
  trend?: MoMTrend;
};

export type ActionCardProps = {
  title: string;
  to?: string;
  rows?: ActionCardRow[];
  isLoading?: boolean;
  disabled?: boolean;
  headerOnly?: boolean;
  children?: React.ReactNode;
  onClickTracking?: () => void;
  testId?: string;
};

export const ActionCard = ({
  title,
  to,
  rows,
  disabled = false,
  isLoading = false,
  headerOnly = false,
  children,
  onClickTracking,
  testId,
}: ActionCardProps) => {
  const { t } = useTranslation(["groupManagement"]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="action-card__rows">
          <LeafRectangleLoader isLoading={true} numRows={2} height="1.5rem" width="60%" margin="0" />
        </div>
      );
    }

    if (disabled) {
      return (
        <div className="action-card__rows">
          <span className="action-card__not-available">{t("notAvailable", defaultContent.notAvailable as string)}</span>
        </div>
      );
    }

    if (rows && rows.length > 0) {
      return (
        <div className="action-card__rows">
          {rows.map((row) => (
            <div key={row.kpiName} className="action-card__row">
              <div className="action-card__row-kpi">
                <span className="action-card__kpi-value">{row.kpiValue}</span>
                <span className="action-card__kpi-name">{row.kpiName}</span>
                {row.trend && (
                  <TrendIndicator
                    trend={row.trend.trend === "HIGHER" ? "higher" : row.trend.trend === "LOWER" ? "lower" : "equal"}
                    difference={row.trend.difference}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <div className="action-card__rows">{children}</div>;
  };

  const renderDisabledState = () => {
    if (!disabled) return null;
    return (
      <div className="action-card__rows">
        <span className="action-card__not-available">{t("notAvailable", defaultContent.notAvailable as string)}</span>
      </div>
    );
  };

  return (
    <div
      className={`action-card${disabled ? " action-card--disabled" : ""}`}
      {...(testId ? { "data-testid": testId } : {})}
    >
      <div className="action-card__header">
        {to && !disabled ? (
          <Link
            to={to}
            className="action-card__title-link"
            onClick={onClickTracking}
            {...(testId ? { "data-testid": `${testId}-chevron-link` } : {})}
          >
            <span className="action-card__title">{title}</span>
            <IconArrowLink weight="thin" />
          </Link>
        ) : (
          <span className="action-card__title">{title}</span>
        )}
      </div>

      {headerOnly ? (
        <>
          {disabled && (
            <>
              <hr className="action-card__divider" />
              {renderDisabledState()}
            </>
          )}
        </>
      ) : (
        <>
          <hr className="action-card__divider" />
          {renderContent()}
        </>
      )}
    </div>
  );
};
